// RTK's own public action matcher, which `identityRejectionTarget` runs on. It buys no eager weight: this module
// is lazy (a feature chunk is the only thing that reaches it) and already value-imports RTK transitively through
// `../listener.middleware` — verify with the standard `app.libs` diff anyway.
import { isRejectedWithValue } from '@reduxjs/toolkit';

import { isIdentityChangedError } from '../identity.scope';
import { readIdentity } from '../identity.snapshot';
// The one non-type import from the listener module: the standing repair listener this module arms
// (`registerIdentityRejectionRepair`) needs the shared `startAppListening`.
import { type AppThunkDispatch, startAppListening } from '../listener.middleware';

import type { IdentityPolicyApi } from './contract';
import { registry } from './registry';
import { apiAccess, letJoinedConsumersSettle, type RunningQuery } from './rtk.access';

/**
 * Test-only seam: un-arm the repair listener and forget which keys it has repaired (every api's latch slots, since
 * clearing the outer map drops the per-api maps with it), so each case can arm it against its own store. Pair it
 * with `listenerMiddleware.clearListeners()` — this clears the module flag, not the registration RTK is holding.
 * Never call it from production code.
 */
export function __resetIdentityRejectionRepairForTests(): void {
    rejectionRepairArmed = false;
    repairedGenerations.clear();
}

/** Whether the repair listener has been registered — module-level, because the registration is app-wide. */
let rejectionRepairArmed = false;

/**
 * Per api: `queryCacheKey` -> the identity generation the repair already acted on for that key.
 *
 * Keyed by api FIRST because an RTKQ cache key is `endpointName(serializedArgs)` — it carries no reducer path, so
 * two registered apis that happen to declare the same endpoint name would share one latch slot and the second one
 * to reject would be starved of its repair. Unreachable while every feature injects into the one shared
 * `baseApi`, but the registry is keyed by api instance precisely so it does not have to be, and an invariant that
 * holds only for a single api is not the invariant this claims to enforce.
 */
const repairedGenerations = new Map<IdentityPolicyApi, Map<string, number>>();

/** One cache entry the repair listener may have to act on, resolved from a rejected action. */
interface RestingRejection {
    api: IdentityPolicyApi;
    endpointName: string;
    /** The endpoint's original argument — what `initiate`/`select` are keyed by. */
    arg: unknown;
    queryCacheKey: string;
}

/**
 * Arm the standing repair listener that enforces the invariant this whole module rests on: **no cache entry of a
 * policy-covered endpoint is left resting at an identity-fence rejection under a generation that is still
 * current.**
 *
 * The commit reconcile alone cannot guarantee that. It repairs the entries it can SEE at commit time, but a
 * fenced rejection can come to rest afterwards, or on a request the reconcile had no way to know about — a
 * request started inside the microtask between another request's outcome being reduced and RTKQ dropping it from
 * its running-query map is not tracked at all, so `getRunningQueryThunk` reports the key as quiet and the
 * purge's own forced refetch is condition-bailed onto it. Whatever the route, the end state is identical and
 * terminal: rejected, holding the empty patch, the transition's generation already marked reconciled, purge-class
 * tags deliberately not invalidated, and a subscribed hook does not re-initiate on a rejection. Reacting to the
 * REJECTION rather than to the transition covers every route into that state, whatever produced it — which is the
 * point of siting the repair here instead of adding another special case to the purge.
 *
 * Idempotent, and armed from `declareIdentityPolicy` — see there for why that is the arming point.
 *
 * LOOP SAFETY, which a self-repairing listener has to argue rather than assume:
 * - The latch allows one repair per cache key per identity GENERATION. A repair that is itself fenced can only
 *   have been fenced by a NEWER transition, so its rejection arrives under a new generation — a different latch
 *   slot, so it is allowed to repair once more, and each transition still pays for at most one repair per key.
 * - The repair is DEFERRED past the rejection's own consumers (`letAwaitingConsumersSettle`), because a rejected
 *   action reaches a listener effect synchronously while the consumer awaiting that request has not read the entry
 *   yet. Without the deferral the repair's `pending` lands FIRST, and `unwrap()` — which does not treat `pending`
 *   as a failure — hands that consumer a successful `undefined` (cold entry) or the PREVIOUS identity's data (warm
 *   one), so `QueryReadResult` reports `identityRejected: false` on a read the fence rejected. Same false success
 *   `letJoinedConsumersSettle` exists to prevent on the abort path, reached from the other side.
 * - The entry-state re-check makes the listener yield rather than pile on: by the time an effect runs, that
 *   newer transition's own reconcile has usually already force-refetched the key, so the entry is no longer
 *   resting at a rejection and the listener does nothing.
 * - It never reacts to its own dispatch: a repair that succeeds is a `fulfilled` action, and one that is
 *   condition-bailed produces `rejected` with `meta.condition` — neither carries a rejected VALUE.
 *
 * The residual this once documented — a repair condition-bailed onto an untracked request, spending the
 * generation's one repair for the key while starting nothing — is PRECLUDED as built, twice over. The rest-check
 * precedes the latch in one synchronous span, so a pending entry yields before anything is spent, and on a
 * non-pending entry a forced initiate never condition-bails (RTKQ's condition checks `status === 'pending'`
 * before it considers force). And the deferral above moves the repair's own initiate past RTKQ's
 * settled-request map-delete, so the repair's request is TRACKED — the settled-but-undeleted window the
 * bailed-repair scenario needed is exactly what `letAwaitingConsumersSettle` waits out. The overlap that remains
 * (a commit reconcile displacing a repair already in flight under the new identity) costs one aborted request,
 * whose consumers get the classifiable identity abort, and every interleaving resolves to a single surviving
 * request — traced, not assumed.
 */
export function registerIdentityRejectionRepair(): void {
    if (rejectionRepairArmed) {
        return;
    }
    rejectionRepairArmed = true;
    startAppListening({
        // Resolved twice (predicate, then effect) rather than smuggled between them: the predicate must stay a
        // pure boolean, and the resolution is a few property reads against the registry.
        predicate: action => identityRejectionTarget(action) !== undefined,
        effect: async (action, listenerApi) => {
            const target = identityRejectionTarget(action);
            if (!target) {
                return;
            }
            // Load-bearing, and the reason this effect is async at all — see there. Everything the repair does,
            // including its own entry re-check, waits until the rejection's own consumers have read the entry.
            await letAwaitingConsumersSettle(target, listenerApi.dispatch);
            repairRestingRejection(target, listenerApi.dispatch, listenerApi.getState);
        },
    });
}

/**
 * The rejected action this listener acts on, matched through RTK's OWN matchers.
 *
 * Two of them, and between them they say everything: RTK's public `isRejectedWithValue` (a rejected thunk
 * carrying a payload) and the api's own generated `matchRejected`, which pins the action to exactly this api AND
 * this endpoint. The api's matcher is what makes the attribution trustworthy — it compares against that api's own
 * query thunk, so nothing here has to know how RTKQ names it. The suffix-slicing this replaced hard-coded that
 * internal name (`${reducerPath}/executeQuery/rejected`) and then reverse-engineered the reducer path out of it.
 * The `meta.arg` fields below are still read structurally, because they are public action contract and there is no
 * matcher for "which cache key".
 *
 * Matched: a rejected query thunk for an api in the registry, carrying an identity error as its rejected VALUE
 * (which is what a fenced settle produces — `identityScoped` returns `{ error }` rather than throwing precisely
 * so this survives as a value), for an endpoint whose declared policy is `purge` or `revalidate`. An `invariant`
 * endpoint is skipped: nothing about it is identity-scoped, so nothing there is the fence's business. So is an
 * endpoint with no policy at all — an unrepaired entry there is the declaration gate's problem to report, not
 * this listener's to paper over.
 */
function identityRejectionTarget(action: unknown): RestingRejection | undefined {
    if (!isRejectedWithValue(action) || !isIdentityChangedError(action.payload)) {
        return undefined;
    }
    const { endpointName, originalArgs, queryCacheKey } = (action.meta.arg ?? {}) as {
        endpointName?: unknown;
        originalArgs?: unknown;
        queryCacheKey?: unknown;
    };
    if (typeof endpointName !== 'string' || typeof queryCacheKey !== 'string') {
        return undefined;
    }
    for (const [api, policies] of registry) {
        const policy = policies.get(endpointName);
        // `?.`: in production a mis-declared name (no such endpoint on this api) is logged rather than thrown
        // and still registers, so this predicate — which runs on every action — must not trust the lookup.
        if (policy && policy.kind !== 'invariant' && apiAccess(api).endpoints[endpointName]?.matchRejected(action)) {
            return { api, endpointName, arg: originalArgs, queryCacheKey };
        }
    }
    return undefined;
}

/**
 * Yield until every consumer awaiting the request that JUST rejected has read the entry, before the repair is
 * allowed to touch it. The rejection-repair counterpart of `letJoinedConsumersSettle`, and the reason the repair
 * effect is async: a rejected action reaches a listener effect SYNCHRONOUSLY, a whole read earlier than the
 * `unwrap()` awaiting that same request — see `registerIdentityRejectionRepair` for the false success that buys.
 *
 * Two hops, because there are two consumer shapes and the later one sets the bound:
 * - The request's OWN awaiter resolves off `Promise.all([running, thunkResult]).then(selectFromState)`, two
 *   microtasks after the thunk settles.
 * - A plain `initiate()` that was condition-bailed ONTO this request resolves off the same shape with the
 *   rejected request's promise inside it, so it reads one hop later again.
 * Awaiting the running query lands past the first, `letJoinedConsumersSettle` past the second — the identical
 * pair, in the identical order, that `refetchAfterAbort` (`./commit.reconcile`) uses for the abort path, so the
 * subsystem has one arithmetic rather than two (the shared hop lives in `./rtk.access`).
 *
 * The running query is still reachable here: RTKQ drops a settled request from that map off its own promise, a
 * microtask after this effect starts. When it is already gone (a replayed rejection whose request RTKQ no longer
 * tracks) there is no attached consumer to wait for, and the remaining hops are harmless. It RESOLVES rather than
 * rejects, including on abort, so awaiting it needs no catch. Should the map hold a NEWER request instead, the
 * entry is `pending` rather than wedged, and waiting for it is what the repair should do anyway — the re-check
 * below then decides on that request's own outcome.
 */
async function letAwaitingConsumersSettle(target: RestingRejection, dispatch: AppThunkDispatch): Promise<void> {
    const running = (dispatch as (action: unknown) => unknown)(apiAccess(target.api).util.getRunningQueryThunk(target.endpointName, target.arg)) as
        | RunningQuery
        | undefined;
    if (running) {
        await running;
    }
    await letJoinedConsumersSettle();
}

/**
 * Force one resting entry to refetch — the effect half of the invariant.
 *
 * Reads the entry's CURRENT state first, through the endpoint's public `select`: the rejection that triggered
 * this is only a signal, and between it and this effect (which `letAwaitingConsumersSettle` deliberately holds
 * back) the entry may already have been repaired — the commit reconcile's own refetch, another consumer's.
 * `isError` is what says it is still resting, since a rejection's `error` stays on the entry across the next
 * `pending` and would otherwise read as unrepaired forever.
 *
 * The identity generation is read HERE rather than when the rejection arrived, so the latch is spent on the
 * identity the repair actually runs under — the same rule `runIdentityCommitReconcile` follows.
 *
 * `subscribe: false` for the same reason `purge` uses it: a repair must not hold an entry alive.
 */
function repairRestingRejection(target: RestingRejection, dispatch: AppThunkDispatch, getState: () => unknown): void {
    const { endpoints } = apiAccess(target.api);
    const entry = endpoints[target.endpointName].select(target.arg)(getState());
    if (entry.isError !== true || !isIdentityChangedError(entry.error)) {
        return;
    }
    const { generation } = readIdentity();
    const latch = repairLatchFor(target.api);
    if (latch.get(target.queryCacheKey) === generation) {
        return;
    }
    latch.set(target.queryCacheKey, generation);
    // Same cast as `reconcileApi`: RTK types `initiate` against its own RootState/dispatch generics.
    (dispatch as (action: unknown) => unknown)(endpoints[target.endpointName].initiate(target.arg, { subscribe: false, forceRefetch: true }));
}

/** The one api's latch slots, created on that api's first repair (mirrors `getOrCreateApiEntry`). */
function repairLatchFor(api: IdentityPolicyApi): Map<string, number> {
    const existing = repairedGenerations.get(api);
    if (existing) {
        return existing;
    }
    const created = new Map<string, number>();
    repairedGenerations.set(api, created);
    return created;
}
